import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
// Ui3
import Modal from 'ui3/Modal/Modal';
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
import Switch from 'ui3/Switch/Switch';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import CompetitorSearchField from './CompetitorSearchField';
import InviteParticipantModal, {
  InviteParticipantSubmit,
} from './InviteParticipantModal';
// Types
import {
  EMPTY_COMPETITOR_DRAFT,
  EMPTY_FIGHT_DRAFT,
  FightCompetitorDraft,
  FightDraft,
} from './fightCardTypes';
// Styles
import styles from './MatchupsModal.module.scss';

export type MatchupsModalProps = {
  open: boolean;
  onClose: () => void;
  drafts: FightDraft[];
  onSave: (next: FightDraft[]) => void;
  canControl?: boolean;
};

type InviteTarget = { index: number; corner: 'A' | 'B' };

const MatchupsModal = ({
  open,
  onClose,
  drafts,
  onSave,
  canControl = true,
}: MatchupsModalProps) => {
  const [bouts, setBouts] = useState<FightDraft[]>([]);
  const [editingCompetitor, setEditingCompetitor] = useState<string | null>(
    null
  );
  const [inviteTarget, setInviteTarget] = useState<InviteTarget | null>(null);
  const dragIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (open) {
      setBouts(drafts.map((d) => ({ ...d })));
      setEditingCompetitor(null);
      setInviteTarget(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateBout = (index: number, patch: Partial<FightDraft>) => {
    setBouts((prev) =>
      prev.map((bout, i) => (i === index ? { ...bout, ...patch } : bout))
    );
  };

  const handleToggleMainEvent = (index: number, checked: boolean) => {
    setBouts((prev) =>
      prev.map((bout, i) => ({ ...bout, isMainEvent: i === index && checked }))
    );
  };

  const handleRemoveBout = (index: number) => {
    setBouts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddBout = () => {
    setBouts((prev) => [...prev, { ...EMPTY_FIGHT_DRAFT }]);
  };

  const handleDrop = (targetIndex: number) => {
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from === null || from === targetIndex) {
      return;
    }
    setBouts((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };

  const handleSave = () => {
    onSave(
      bouts.filter(
        (bout) => bout.fighterA.name.trim() || bout.fighterB.name.trim()
      )
    );
    onClose();
  };

  const setCompetitor = (
    index: number,
    corner: 'A' | 'B',
    competitor: FightCompetitorDraft
  ) => {
    updateBout(index, {
      [corner === 'A' ? 'fighterA' : 'fighterB']: competitor,
    });
  };

  const handleInviteSubmit = (invite: InviteParticipantSubmit) => {
    if (!inviteTarget) {
      return;
    }
    setCompetitor(inviteTarget.index, inviteTarget.corner, {
      storeId: null,
      name: invite.name,
      avatarURL: null,
      invitedEmail: invite.email,
      inviteToken: invite.inviteToken,
    });
    setEditingCompetitor(null);
  };

  const renderCompetitor = (
    index: number,
    corner: 'A' | 'B',
    bout: FightDraft
  ) => {
    const competitor = corner === 'A' ? bout.fighterA : bout.fighterB;
    const editKey = `${index}-${corner}`;
    const isEditing = editingCompetitor === editKey;

    return (
      <div className={styles.competitor}>
        <div className={styles.competitorHeader}>
          <Text
            variant="body3Medium"
            className={cn(
              styles.competitorLabel,
              corner === 'A' ? styles.competitorLabelA : styles.competitorLabelB
            )}
          >
            Competitor {corner}
          </Text>
          {canControl && (
            <button
              type="button"
              className={styles.inviteButton}
              onClick={() => setInviteTarget({ index, corner })}
            >
              <MillionsIcon name="userPlus" className={styles.inviteIcon} />
              Invite
            </button>
          )}
        </div>
        <Text variant="body3Regular" color="lights-medium">
          Participant
        </Text>

        {competitor.name.trim() && !isEditing ? (
          <div className={styles.participantChip}>
            {competitor.avatarURL ? (
              <img
                className={styles.participantAvatarImage}
                src={competitor.avatarURL}
                alt=""
              />
            ) : (
              <span className={styles.participantAvatar}>
                {competitor.name.trim().charAt(0).toUpperCase()}
              </span>
            )}
            <span className={styles.participantName}>{competitor.name}</span>
            {competitor.inviteToken && (
              <span className={styles.invitedTag}>Invited</span>
            )}
            {canControl && (
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove competitor ${corner}`}
                onClick={() =>
                  setCompetitor(index, corner, { ...EMPTY_COMPETITOR_DRAFT })
                }
              >
                <MillionsIcon name="close" className={styles.chipRemoveIcon} />
              </button>
            )}
          </div>
        ) : (
          <CompetitorSearchField
            name={`bout-${index}-${corner}`}
            disabled={!canControl}
            value={competitor.name}
            onFocus={() => setEditingCompetitor(editKey)}
            onBlur={() =>
              setEditingCompetitor((prev) => (prev === editKey ? null : prev))
            }
            onTextChange={(text) =>
              setCompetitor(index, corner, {
                ...EMPTY_COMPETITOR_DRAFT,
                name: text,
              })
            }
            onSelectStore={(store) => {
              setCompetitor(index, corner, store);
              setEditingCompetitor(null);
            }}
          />
        )}
      </div>
    );
  };

  const renderStepper = (
    value: number,
    suffix: string,
    onChange: (next: number) => void,
    min = 1,
    max = 99
  ) => (
    <div className={styles.stepper}>
      <button
        type="button"
        className={styles.stepperButton}
        aria-label={`Decrease ${suffix}`}
        disabled={!canControl || value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        −
      </button>
      <span className={styles.stepperValue}>{value}</span>
      <button
        type="button"
        className={styles.stepperButton}
        aria-label={`Increase ${suffix}`}
        disabled={!canControl || value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      >
        +
      </button>
      <Text variant="body3Regular" color="lights-medium">
        {suffix}
      </Text>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Matchups"
      titleAlign="left"
      size="medium"
      footer={
        <div className={styles.footer}>
          <Button
            variant="primary"
            size="small"
            onClick={handleSave}
            disabled={!canControl}
          >
            Save Match Card
          </Button>
        </div>
      }
    >
      <div className={styles.bouts}>
        {bouts.map((bout, index) => (
          <div
            key={index}
            className={cn(styles.bout, {
              [styles.boutMainEvent]: bout.isMainEvent,
            })}
            draggable={canControl}
            onDragStart={() => {
              dragIndexRef.current = index;
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => handleDrop(index)}
          >
            <div className={styles.boutHeader}>
              <span className={styles.dragHandle} aria-hidden>
                <MillionsIcon name="dotsVertical" className={styles.dragIcon} />
                <MillionsIcon name="dotsVertical" className={styles.dragIcon} />
              </span>
              <Text
                variant="body3Medium"
                color="lights-medium"
                className={styles.boutTitle}
              >
                Bout {index + 1}
              </Text>
              {bout.isMainEvent && (
                <span className={styles.mainEventBadge}>Main Event</span>
              )}

              <span className={styles.boutHeaderRight}>
                <Switch
                  name={`bout-${index}-main-event`}
                  label="Main Event"
                  checked={bout.isMainEvent}
                  disabled={!canControl}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleToggleMainEvent(index, event.target.checked)
                  }
                />
                {bouts.length > 1 && (
                  <button
                    type="button"
                    className={styles.removeButton}
                    aria-label="Remove bout"
                    disabled={!canControl}
                    onClick={() => handleRemoveBout(index)}
                  >
                    <MillionsIcon name="trash" className={styles.removeIcon} />
                  </button>
                )}
              </span>
            </div>

            <div className={styles.competitors}>
              {renderCompetitor(index, 'A', bout)}
              <span className={styles.vs} aria-hidden>
                VS
              </span>
              {renderCompetitor(index, 'B', bout)}
            </div>

            <Text
              variant="body3Medium"
              color="lights-medium"
              className={styles.roundsKicker}
            >
              Rounds &amp; Timer
            </Text>
            <div className={styles.roundsRow}>
              <div className={styles.roundsField}>
                <Text variant="body3Regular" color="white">
                  Number of Rounds
                </Text>
                {renderStepper(bout.rounds ?? 1, 'Rounds', (next) =>
                  updateBout(index, { rounds: next })
                )}
              </div>
              <div className={styles.roundsField}>
                <Text variant="body3Regular" color="white">
                  Timer
                </Text>
                {renderStepper(bout.roundMinutes ?? 3, 'Minutes', (next) =>
                  updateBout(index, { roundMinutes: next })
                )}
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          className={styles.addBout}
          disabled={!canControl}
          onClick={handleAddBout}
        >
          <MillionsIcon name="plus" className={styles.addBoutIcon} />
          Add Another Matchup
        </button>
      </div>

      <InviteParticipantModal
        open={inviteTarget !== null}
        onClose={() => setInviteTarget(null)}
        onSubmit={handleInviteSubmit}
      />
    </Modal>
  );
};

export default MatchupsModal;
